import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { GuestRoomTab } from '@/components/pages/setting/guest-room/tab'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { useErrorHandler } from '@/hooks/use-error-handler'
import * as api from '@/apis/aipass'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import { Hint } from '@/components/atoms/hint'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { InputField } from '@/components/molecules/input-field'
import { EditFooter } from '@/components/organisms/edit-footer'
import { omit } from 'lodash'
import { AssignOrders, FloorSettings, GuestRoomAutoAssignConfig, Priorities, VacancyPeriods } from '@/models/guest-room/guest-room-assign'

export const SettingGuestRoomAutoAssign: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  const assignOrderItems = [
    { value: AssignOrders.ascending, label: t('Ascending') },
    { value: AssignOrders.descending, label: t('Descending') },
  ]
  const vacancyPeriodItems = [
    { value: VacancyPeriods.longFirst, label: t('Long first') },
    { value: VacancyPeriods.shortFirst, label: t('Short first') },
  ]
  const floorSettingItems = [
    { value: FloorSettings.same, label: t('Same floor') },
    { value: FloorSettings.differentPossible, label: t('Different floors are also possible') },
  ]

  const {
    control,
    reset,
    getValues,
    resetField,
    formState: { isValid, dirtyFields },
  } = useForm<GuestRoomAutoAssignConfig>({
    defaultValues: {
      priorities: [Priorities.assignOrder, Priorities.vacancyPeriod],
      assignOrder: AssignOrders.ascending,
      vacancyPeriod: VacancyPeriods.longFirst,
      floorSetting: FloorSettings.same,
      timingOfImportReservation: false,
      timingOfDatetime: false,
      timingOfDatetimeSetting: {
        day: undefined,
        time: undefined,
      },
      timingOfSelfCheckIn: false,
      timingOfStayAtPms: false,
    },
  })

  const watchTimingOfDatetime = useWatch({ control, name: 'timingOfDatetime' })
  const immediateApplyFields = useWatch({
    control,
    name: [
      'priorities',
      'assignOrder',
      'vacancyPeriod',
      'floorSetting',
      'timingOfImportReservation',
      'timingOfDatetime',
      'timingOfSelfCheckIn',
      'timingOfStayAtPms',
    ],
  })

  const fetchAutoAssignmentSettings = async () => {
    try {
      setIsLoading(true)
      const response = await api.fetchAutoAssignmentSettings()
      reset(response)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
      setIsLoaded(true)
    }
  }

  const applyAutoAssignmentSettings = async () => {
    const formValues = omit(getValues(), ['timingOfDatetimeSetting']) as Omit<GuestRoomAutoAssignConfig, 'timingOfDatetimeSetting'>
    try {
      setIsLoading(true)
      await api.applyAutoAssignmentSettings(formValues)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveAutoAssignmentSettings = async () => {
    const formValues = { timingOfDatetimeSetting: getValues().timingOfDatetimeSetting }
    try {
      setIsLoading(true)
      await api.saveAutoAssignmentSettings(formValues)
      reset(getValues())
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const immediateApplyFieldsStr = useMemo(() => JSON.stringify(immediateApplyFields), [immediateApplyFields])
  const isSaveRequired = useMemo(
    () => !!dirtyFields.timingOfDatetimeSetting && watchTimingOfDatetime,
    [dirtyFields?.timingOfDatetimeSetting, watchTimingOfDatetime],
  )

  const hoursRef = useRef<string[]>(Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0') + ':00'))

  useEffect(() => {
    if (isLoaded) applyAutoAssignmentSettings()
  }, [immediateApplyFieldsStr])

  useEffect(() => {
    fetchAutoAssignmentSettings()
  }, [])

  const footerContent = isSaveRequired ? (
    <EditFooter
      onCancel={() => {
        resetField('timingOfDatetimeSetting')
      }}
      onSave={saveAutoAssignmentSettings}
      isDisabledSave={!isValid}
    />
  ) : undefined

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <TabContainer tabComponent={<GuestRoomTab currentTab="auto-assign" />} isShowFooter={false}>
        <div css={mainContainerStyle}>
          <div>
            <div className="block-title">{t('Priority')}</div>
            <div className="setting-row">
              <div className="order-label-line">
                <Controller
                  name="priorities"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value.findIndex(p => p === Priorities.assignOrder) + 1}
                      onChange={v => {
                        field.onChange(
                          v === 1 ? [Priorities.assignOrder, Priorities.vacancyPeriod] : [Priorities.vacancyPeriod, Priorities.assignOrder],
                        )
                      }}
                      customStyle={css({ paddingBottom: 0, width: 62 })}
                    >
                      <AntSelect.Option value={1}>1</AntSelect.Option>
                      <AntSelect.Option value={2}>2</AntSelect.Option>
                    </Select>
                  )}
                />
                <div className="label-text">{t('Assign sequence')}</div>
              </div>
              <div>
                <Controller
                  name="assignOrder"
                  control={control}
                  render={({ field }) => (
                    <RadioGroupField
                      {...field}
                      items={assignOrderItems}
                      groupCss={{ width: 300 }}
                      itemWrapperCss={{ width: '50%' }}
                      itemCss={{ fontWeight: 'normal' }}
                      style={{ radioSize: 18 }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="setting-row">
              <div className="order-label-line">
                <Controller
                  name="priorities"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value.findIndex(p => p === Priorities.vacancyPeriod) + 1}
                      onChange={v => {
                        field.onChange(
                          v === 1 ? [Priorities.vacancyPeriod, Priorities.assignOrder] : [Priorities.assignOrder, Priorities.vacancyPeriod],
                        )
                      }}
                      customStyle={css({ paddingBottom: 0, width: 62 })}
                    >
                      <AntSelect.Option value={1}>1</AntSelect.Option>
                      <AntSelect.Option value={2}>2</AntSelect.Option>
                    </Select>
                  )}
                />
                <div className="label-text">{t('Vacancy period')}</div>
                <Hint
                  customCss={css({
                    left: -8,
                    '.help-popup': { left: 16, top: 0 },
                  })}
                >
                  <div css={vacancyPeriodHintStyle}>
                    <div css={{ marginBottom: 24 }}>
                      <div className="title">{t('Longest vacancy period')}</div>
                      <div>{t('Preferentially assigned rooms that have not been occupied immediately before')}</div>
                    </div>
                    <img src={require('@/static/images/room/vacancyPeriodHint-2.jpg')} />
                    <div css={{ marginBottom: 24, marginTop: 32 }}>
                      <div className="title">{t('Shortest vacancy period')}</div>
                      <div>{t('Preferential assignment from the most recently used room')}</div>
                    </div>
                    <img src={require('@/static/images/room/vacancyPeriodHint-1.jpg')} />
                  </div>
                </Hint>
              </div>
              <div>
                <Controller
                  name="vacancyPeriod"
                  control={control}
                  render={({ field }) => (
                    <RadioGroupField
                      {...field}
                      items={vacancyPeriodItems}
                      groupCss={{ width: 300 }}
                      itemWrapperCss={{ width: '50%' }}
                      itemCss={{ fontWeight: 'normal' }}
                      style={{ radioSize: 18 }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="block-title">{t('Other conditions')}</div>
            <div className="setting-row">
              <div className="order-label-line">
                <div className="label-text">{t('Reservation of 2 or more rooms')}</div>
              </div>
              <div>
                <Controller
                  name="floorSetting"
                  control={control}
                  render={({ field }) => (
                    <RadioGroupField
                      {...field}
                      items={floorSettingItems}
                      groupCss={{ width: 300 }}
                      itemWrapperCss={{ width: '50%' }}
                      itemCss={{ fontWeight: 'normal' }}
                      style={{ radioSize: 18 }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="block-title">{t('Auto assignment timing')}</div>
            <div className="setting-row">
              <div className="order-label-line">
                <div className="label-text">{t('At reservation creation time')}</div>
              </div>
              <div>
                <Controller
                  name="timingOfImportReservation"
                  control={control}
                  render={({ field }) => <ToggleButton label={{ on: 'ON', off: 'OFF' }} {...field} />}
                />
              </div>
            </div>
            <div className="setting-row" style={watchTimingOfDatetime ? { borderBottom: 'none' } : {}}>
              <div className="order-label-line">
                <div className="label-text">{t('At the specified date and time')}</div>
              </div>
              <div>
                <Controller
                  name="timingOfDatetime"
                  control={control}
                  render={({ field }) => <ToggleButton label={{ on: 'ON', off: 'OFF' }} {...field} />}
                />
              </div>
            </div>
            {watchTimingOfDatetime && (
              <div className="setting-row -inline-detail">
                <Trans
                  i18nKey="beforeCheckinTime"
                  components={{
                    l: <div className="label-text" />,
                    days: (
                      <Controller
                        name="timingOfDatetimeSetting.day"
                        control={control}
                        rules={{
                          required: 'Required field has not been entered',
                          pattern: {
                            value: /^[0-9]+$/,
                            message: t('Please enter numbers only, without symbols or spaces'),
                          },
                        }}
                        render={({ field: { onChange, value, name } }) => (
                          <InputField
                            marginBottom={0}
                            placeholder="0"
                            type="tel"
                            value={value ?? ''}
                            name={name}
                            handleChangeData={onChange}
                            width="60px"
                            fieldWidth="60px"
                          />
                        )}
                      />
                    ),
                    hours: (
                      <Controller
                        name="timingOfDatetimeSetting.time"
                        control={control}
                        rules={{ required: 'Required field has not been entered' }}
                        render={({ field: { onChange, value, name } }) => (
                          <Select placeholder="00:00" value={value} onChange={onChange} customStyle={css({ paddingBottom: 0, width: 92 })}>
                            {hoursRef.current.map(time => (
                              <AntSelect.Option key={time} value={time}>
                                {time}
                              </AntSelect.Option>
                            ))}
                          </Select>
                        )}
                      />
                    ),
                  }}
                />
              </div>
            )}
            <div className="setting-row">
              <div className="order-label-line">
                <div className="label-text">{t('At completion of self check-in')}</div>
              </div>
              <div>
                <Controller
                  name="timingOfSelfCheckIn"
                  control={control}
                  render={({ field }) => <ToggleButton label={{ on: 'ON', off: 'OFF' }} {...field} />}
                />
              </div>
            </div>
            <div className="setting-row">
              <div className="order-label-line">
                <div className="label-text">{t('At time of change during the stay in PMS')}</div>
              </div>
              <div>
                <Controller
                  name="timingOfStayAtPms"
                  control={control}
                  render={({ field }) => <ToggleButton label={{ on: 'ON', off: 'OFF' }} {...field} />}
                />
              </div>
            </div>
          </div>
        </div>
      </TabContainer>
    </SettingsLayout>
  )
}

const mainContainerStyle = css({
  paddingTop: 75,

  '.block-title': {
    background: '#F2F2F2 0% 0% no-repeat padding-box',
    height: 32,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 1.2,
    color: '#676767',
    alignContent: 'center',
    padding: '0 32px',
  },
  '.setting-row': {
    padding: '0 32px',
    display: 'flex',
    justifyContent: 'space-between',
    height: 64,
    alignItems: 'center',
    borderBottom: '1px solid #F2F2F2',
    '.order-label-line': {
      display: 'flex',
      alignItems: 'center',
      gap: 24,
      '.label-text': {
        fontSize: 14,
        fontWeight: 'bold',
        letterSpacing: 1.4,
        color: '#272727',
      },
    },
    '&.-inline-detail': {
      justifyContent: 'end',
      gap: 8,
      paddingBottom: 32,
      '.label-text': {
        fontSize: 14,
        letterSpacing: 1.4,
        color: '#272727',
      },
    },
  },
})

const vacancyPeriodHintStyle = css({
  width: 600,
  padding: 32,
  img: { width: '100%' },
  '.title': {
    color: '#F2A40B',
    fontWeight: 'bold',
    marginBottom: 16,
  },
})
